import { z as builder } from "zod"
import { useForm } from "react-hook-form"
import { useCallback, useState } from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { authenticate } from "@/actions/auth"
import { loginSchema } from "@/schemas/loginSchema"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { useToken } from "@/hooks/useToken"
import { Spinner } from "@/components//ui/spinner"
import { EyeOff, Eye } from "lucide-react"

import { toast } from "sonner"

const LoginForm = () => {
    const [token] = useToken()
    const [loading, setLoading] = useState(false)
    const [passwordVisible, setPasswordVisible] = useState(false)

    const login = useForm<builder.infer<typeof loginSchema>>({
        resolver: zodResolver(loginSchema),
    })

    const loginValidity = useCallback(async (values: builder.infer<typeof loginSchema>) => {
        setLoading(true)
        const { data, error } = await authenticate({ ...values, id: token })

        if (error) {
            setLoading(false)
            toast("Fallo al iniciar sesión")
            return
        }

        location.replace(data!.redirectTo)
    }, [token])

    const togglePassword = useCallback(() => setPasswordVisible(current => !current), [])

    return (
        <Form {...login}>
            <form onSubmit={login.handleSubmit(loginValidity)} className="space-y-8">
                <FormField
                    control={login.control}
                    name="username"
                    render={({ field }) => (
                        <FormItem className="mb-6">
                            <FormControl>
                                <Input className="bg-gray-50 h-12" autoComplete="username" placeholder="Usuario" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={login.control}
                    name="password"
                    render={({ field }) => (
                        <FormItem className="mb-6">
                            <FormControl>
                                <div className="relative">
                                    <Input
                                        className="bg-gray-50 h-12"
                                        type={passwordVisible ? "text" : "password"}
                                        autoComplete="current-password"
                                        placeholder="Contraseña" {...field} />
                                    <Button type="button" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8" variant="ghost" onClick={togglePassword}>
                                        {passwordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <Button data-loading={loading} disabled={loading} className="w-full data-[loading='true']:pointer-events-none data-[loading='true']:bg-red-500/85  bg-red-500 h-10 hover:bg-red-600 cursor-pointer" type="submit">
                    {loading && <Spinner />}
                    {loading ? "Cargando..." : "Ingresar"}
                </Button>
            </form>
        </Form>
    )
}

export default LoginForm